import type { ReactNode } from 'react';
import { ArtifactLink } from '../../components/ArtifactLink';
import type { ArtifactType } from '@thottbot/shared';

export interface FieldRow {
  label: string;
  value: ReactNode;
}

/** Two-column key/value table; rows with an empty value are omitted. */
export function FieldTable({ rows }: { rows: FieldRow[] }) {
  const visible = rows.filter(
    (r) => r.value !== undefined && r.value !== null && r.value !== '' && r.value !== false,
  );
  if (visible.length === 0) return <p className="muted">No details recorded yet.</p>;
  return (
    <div className="table-scroll">
      <table className="grid fields">
        <tbody>
          {visible.map((r) => (
            <tr key={r.label}>
              <th>{r.label}</th>
              <td>{r.value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** A stored cross reference, possibly enriched with `slug`/`refType` by the API. */
export interface RefView {
  artifactId?: string;
  gameId?: number;
  name: string;
  slug?: string;
  refType?: ArtifactType;
  chance?: number;
  /** Addon observations: times seen, the source's loot events, vendor price (copper), build. */
  count?: number;
  looted?: number;
  price?: number;
  build?: string;
}

/**
 * A cross reference with what the addon observed about it: "N% (M looted)" when the loot
 * denominator is known (from the ref or the list), "(N%)" for seeded chances, the vendor
 * price, and the build the link was last seen in.
 */
export function RefLink({
  r,
  fallbackType,
  looted,
}: {
  r: RefView;
  fallbackType: ArtifactType;
  looted?: number;
}) {
  const type = r.refType ?? fallbackType;
  const denominator = r.looted ?? looted;
  const chance =
    r.chance !== undefined
      ? denominator !== undefined
        ? `${formatChance(r.chance)} (${denominator} looted)`
        : `(${formatChance(r.chance)})`
      : null;
  const extras = [
    chance,
    r.price !== undefined ? <Money key="price" copper={r.price} /> : null,
    r.build ? `seen in ${r.build}` : null,
  ].filter((x) => x !== null);
  // Unresolved refs (no published page yet) stay plain text: no link and no tooltip.
  const anchor = r.slug ? (
    <ArtifactLink type={type} slug={r.slug} name={r.name} />
  ) : (
    <span title={r.gameId ? `Game id ${r.gameId}` : undefined}>{r.name}</span>
  );
  return (
    <>
      {anchor}
      {extras.map((x, i) => (
        <span key={i} className="muted ref-extra">
          {' '}
          {x}
        </span>
      ))}
    </>
  );
}

export function RefList({
  refs,
  fallbackType,
  looted,
}: {
  refs: RefView[] | undefined;
  fallbackType: ArtifactType;
  /** Loot events of the source, the denominator for every `chance` in the list. */
  looted?: number;
}) {
  if (!refs || refs.length === 0) return null;
  return (
    <>
      {refs.map((r, i) => (
        <span key={`${r.artifactId ?? r.gameId ?? r.name}-${i}`}>
          {i > 0 && ', '}
          <RefLink r={r} fallbackType={fallbackType} looted={looted} />
        </span>
      ))}
    </>
  );
}

/** A `FieldTable` value for a ref list: `undefined` (row omitted) when the list is empty. */
export function refList(refs: unknown, fallbackType: ArtifactType, looted?: number): ReactNode {
  const list = Array.isArray(refs) ? (refs as RefView[]) : undefined;
  if (!list || list.length === 0) return undefined;
  return <RefList refs={list} fallbackType={fallbackType} looted={looted} />;
}

/** A `FieldTable` value for prose: `undefined` (row omitted) when the text is empty. */
export function prose(text: string | undefined): ReactNode {
  return text ? <Prose text={text} /> : undefined;
}

function formatChance(c: number): string {
  return c >= 10 ? `${Math.round(c)}%` : `${c.toFixed(1)}%`;
}

export function Money({ copper }: { copper: number | undefined }) {
  if (!copper) return null;
  const g = Math.floor(copper / 10000);
  const s = Math.floor((copper % 10000) / 100);
  const c = copper % 100;
  return (
    <span>
      {g > 0 && `${g}g `}
      {(s > 0 || g > 0) && `${s}s `}
      {c}c
    </span>
  );
}

export function Prose({ text }: { text: string | undefined }) {
  if (!text) return null;
  return <div style={{ whiteSpace: 'pre-wrap' }}>{text}</div>;
}
